import BaseIconSvg from '@/components/bases/BaseIconSvg';
import { Modal } from 'antd';
import { ModalFuncProps } from 'antd/lib';
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';

export interface ICustomModalFuncProps extends ModalFuncProps {
  minimizeContainer?: Element | null;
}
export interface IQuickModal {
  error: (options: ModalFuncProps) => void;
  confirm: (props: ModalFuncProps) => Promise<boolean>;
  info: (props: ModalFuncProps) => IQuickModalResult;
  infoAsync: (props: ModalFuncProps) => Promise<boolean>;
  custom: (props: ICustomModalFuncProps) => IQuickModalResult;
}

export interface IQuickModalResult {
  destroy: () => void;
  update: (configUpdate: ModalFuncProps) => void;
  minimize?: () => void;
  open?: () => void;
  close: () => void;
  confirmHandler?: () => Promise<boolean>;
}

export interface quickModalContextType {
  modal: IQuickModalResult;
}

const showUpModal = (e: any) => {
  const startZIndex = 500;
  const element = (e?.target || e)?.closest('.ant-modal-root')?.parentNode;

  if (element) {
    const children = Array.from(element.parentNode.children).filter(
      (c: any) => c.querySelector('.ant-modal-root') !== null,
    );
    const siblings = children.filter((c) => c !== element).sort((a: any, b: any) => a.style.zIndex - b.style.zIndex);

    element.style.position = 'relative';
    element.style.zIndex = startZIndex + children.length;

    siblings.forEach((s: any, index: number) => {
      s.style.position = 'relative';
      s.style.zIndex = startZIndex + index;
    });
  }
};

const genMethod = (modal: any = window.modal): IQuickModal => {
  return {
    error: (options: ModalFuncProps) => {
      modal = modal || window.modal;
      modal.error({
        ...options,
        icon: <></>,
        title: <div className="uppercase border-b">Thông báo</div>,
        okButtonProps: {
          title: 'Đóng',
          className: 'w-24 bg-gradient-1 rounded-lg',
        },
      });
    },
    confirm: (props: ModalFuncProps) =>
      new Promise((resolve) => {
        modal = modal || window.modal;
        modal.confirm({
          icon: <></>,
          title: 'Thông báo',
          content: 'Bạn chắc chắn muốn xóa',
          okButtonProps: { className: 'bg-orange-8 hover:!bg-orange-6' },
          cancelText: 'Không',
          okText: 'Có',
          onOk: () => {
            resolve(true);
          },
          ...props,
        });
      }),

    infoAsync: (props: ModalFuncProps) =>
      new Promise((resolve) => {
        modal = modal || window.modal;
        modal.info({
          icon: <></>,
          title: 'Thông báo',
          content: '',
          okButtonProps: { className: 'bg-orange-8 hover:!bg-orange-6' },
          onOk: () => {
            resolve(true);
          },
          ...props,
        });
      }),
    info: (props: ModalFuncProps) => {
      modal = modal || window.modal;
      return modal.info({
        icon: <></>,
        title: 'Thông báo',
        content: '',
        wrapClassName: '',
        className: '',
        rootClassName: '',
        okButtonProps: { className: 'bg-orange-8 hover:!bg-orange-6' },
        ...props,
      });
    },
    custom: ({ title, content, minimizeContainer, width, modalRender, ...props }: ICustomModalFuncProps) => {
      modal = modal || window.modal;
      props.mask = props.mask === undefined ? (props.mask = true) : props.mask;
      const modalResult: IQuickModalResult = modal.info({
        width: width,
        icon: <></>,
        footer: false,
        wrapClassName: `[&_.ant-modal-confirm-content]:w-full [&_.ant-modal-content]:p-0 [&_.ant-modal-content]:!bg-quick-modal [&_.ant-modal-close]:hidden [&_.ant-modal-confirm-paragraph]:max-w-[100%] ${
          !props.mask ? 'pointer-events-none' : ''
        }`,
        modalRender(node: React.ReactNode) {
          node = modalRender ? modalRender(node) : node;
          return (
            <DragComponent
              content={content}
              minimizeContainer={minimizeContainer}
              modalResult={modalResult}
              node={node}
              title={title}
            />
          );
        },
        title: '',
        ...props,
        content: '',
        className: '',
      });
      return modalResult;
    },
  };
};

const quickModal: IQuickModal = genMethod();

export const useQuickModal = (): { quickModal: IQuickModal; quickModalContext: any } => {
  const [modal, quickModalContext] = Modal.useModal();

  return { quickModal: genMethod(modal), quickModalContext };
};

export default quickModal;

const quickModalContext = React.createContext<quickModalContextType | any>({});

export const useQuickModalContext = () => React.useContext<quickModalContextType>(quickModalContext);

const DragComponent = ({
  modalResult,
  minimizeContainer,
  title,
  content,
  node,
}: {
  modalResult: IQuickModalResult;
  minimizeContainer: any;
  title: React.ReactNode;
  content: React.ReactNode;
  node: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [disabled, setDisabled] = useState(false);
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = ref.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };
  useEffect(() => {
    const container = document.createElement('div');

    modalResult.close = async () => {
      if (!modalResult.confirmHandler || (await modalResult.confirmHandler())) {
        modalResult.destroy();
        modalResult.open = undefined;
      }
    };
    modalResult.open = () => {
      showUpModal(ref.current);
      container.remove();
      modalResult.update({ className: '', open: true });
    };
    modalResult.minimize = () => {
      modalResult.update({ className: 'hidden' });

      if (minimizeContainer) {
        const customTitle: any = title;
        const MinimizeBar = () => {
          return (
            <div className="flex items-center  modal-minimize  p-2 bg-gray-4 rounded-lg rounded-tr-lg mr-1">
              <div className="w-32 line-clamp-1 mr-1">{customTitle?.props?.title || customTitle}</div>
              <BaseIconSvg
                onClick={() => {
                  container.remove();
                  modalResult.update({ className: '' });
                }}
                src="/icons/Open.svg"
                className="mr-2 minimize-open cursor-pointer"
              />
              <BaseIconSvg
                className="cursor-pointer"
                src="/icons/Regular/Close - icon.svg"
                onClick={() => {
                  container.remove();
                  modalResult.close ? modalResult.close() : modalResult.destroy();
                }}
              />
            </div>
          );
        };
        container.classList.add('minimize-bar-container');
        const root = createRoot(container);
        root.render(<MinimizeBar />);
        minimizeContainer.appendChild(container);
      }
    };
    modalResult.update({
      content: (
        <quickModalContext.Provider value={{ modal: modalResult }}>
          <>
            <div className="h-full flex flex-col max-h-[85vh] w-full overflow-auto ">
              <div className="quick-modal-header w-full mb-2">
                <div className="flex justify-between">
                  <div
                    className="quick-modal-title move-modal-base cursor-move w-full p-4"
                    onMouseOver={() => {
                      if (disabled) {
                        setDisabled(true);
                      }
                    }}
                    onMouseOut={() => {
                      setDisabled(false);
                    }}
                  >
                    {typeof title === 'string' ? <div className="label-large">{title}</div> : title}
                  </div>
                  <div className="quick-modal-action flex flex-none p-4">
                    {minimizeContainer && (
                      <BaseIconSvg
                        src="/icons/Minus.svg"
                        className="wh-[16px] mr-4 cursor-pointer hover:text-mint-8"
                        onClick={() => {
                          modalResult.minimize && modalResult.minimize();
                        }}
                      />
                    )}
                    <BaseIconSvg
                      className="wh-[16px] icon-close-modal cursor-pointer hover:text-mint-8"
                      onClick={() => {
                        modalResult.close ? modalResult.close() : modalResult.destroy();
                      }}
                      src="/icons/Regular/Close - icon.svg"
                    />
                  </div>
                </div>
                <div className="px-4">
                  <div className="border-b"></div>
                </div>
              </div>
              <div className="pr-1 overflow-auto">{content}</div>
            </div>
          </>
        </quickModalContext.Provider>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Draggable
      disabled={disabled}
      bounds={bounds}
      handle=".move-modal-base"
      nodeRef={ref}
      onStart={(event, uiData) => onStart(event, uiData)}
    >
      <div ref={ref} onClick={showUpModal} onTouchStart={showUpModal}>
        {node}
      </div>
    </Draggable>
  );
};
